import { canAccessAdminShell } from '@versports/access'
import type { MeResponse } from '../api/auth'

/**
 * A dónde mandar a alguien recién logueado. Fuente única para las dos
 * superficies que lo deciden: `LoginPage` (después de `login()` / del login
 * por código del jugador), `KnowQuestionnaire` (después de enviar) y el tab
 * VR de la barra persistente (`AppNavBar`). Vive acá y no copiado en cada
 * caller para que las puertas al juego no se separen.
 */

/**
 * `?next=` sólo se respeta si es una ruta interna. `/` se descarta a
 * propósito: es el destino GENÉRICO con el que `RequireAuth` rebota a
 * cualquiera que entre a la home, así que dejarlo ganar anularía el ruteo
 * por rol. Un `?next=` a una ruta concreta (`/club`, `/data/…`) sí gana.
 */
export function safeNextPath(raw: string | null | undefined): string | null {
  if (!raw) return null
  if (!raw.startsWith('/') || raw.startsWith('//')) return null
  if (raw === '/') return null
  return raw
}

/** `?next=` que apunta a una superficie de staff/admin (ahí rebotan
 *  `RequireAdminShell` y el `RequireAuth` de `/club`). `/login` abre
 *  directamente el formulario de manager cuando esto es `true`. */
export function isStaffNext(raw: string | null | undefined): boolean {
  const p = safeNextPath(raw)
  if (!p) return false
  return (
    p === '/admin' || p.startsWith('/admin/') || p === '/club' || p.startsWith('/club/')
  )
}

/** Same account policy as the route gates; includes the devices-only shell. */
export function hasAdminAreaAccess(user: MeResponse): boolean {
  return canAccessAdminShell(user)
}

function isClubStaff(user: MeResponse): boolean {
  return (user.club_memberships?.length ?? 0) > 0
}

/**
 * Cuenta SIN CLUB: no puede hacer nada hasta que el staff de un club la dé de
 * alta (decisión de mg, 2026-09-09 — "solo si estás asignado a un club
 * deberías poder avanzar o hacer cualquier cosa"). Es el estado de cualquiera
 * que se registra solo: desde esa fecha el alta pública ya no lo mete en el
 * club default.
 *
 * `club_required` del backend dice "no puede JUGAR", que es cierto también
 * para un manager sin perfil de jugador y para un superadmin. Acá se agregan
 * las dos excepciones de RUTEO: al staff le queda `/club` y al superadmin
 * `/admin`, así que la pantalla de bloqueo (`pages/NoClubPage.tsx`) es sólo
 * para el jugador suelto. Para JUGAR igual necesitan club: eso lo resuelve el
 * gate del vr-app, no esta función.
 */
export function isClubless(user: MeResponse): boolean {
  return user.club_required && !isClubStaff(user) && !hasAdminAreaAccess(user)
}

/** Ruta de la pantalla de bloqueo. Única superficie de una cuenta sin club. */
export const NO_CLUB_ROUTE = '/sin-club'

/**
 * Si al jugador todavía le falta firmar el consentimiento. El staff de club
 * está EXIMIDO (mismo criterio que `/consent` y que `profile_completion.py`
 * en el backend, que ya resuelve `consent_ok` con esa exención).
 */
export function needsConsent(user: MeResponse): boolean {
  return !user.consent_ok && !isClubStaff(user)
}

/**
 * Ruta del gate de consentimiento (`/consent`, ver `pages/ConsentGate.tsx`),
 * llevándose el destino final colgado en `?next=`: al aceptar, la página
 * navega ahí sola. Es lo que hace que el orden CONSENTIMIENTO →
 * CUESTIONARIO valga también en la web (regla de producto, doc 32 §4; en la
 * tablet `/tablet` el orden ya era ese).
 *
 * Sólo lo usa el camino de CUESTIONARIOS en la web (`RequireConsent` sobre
 * `/data/:code` y el `?next=` de una invitación). El camino de JUEGO ya no
 * pasa por acá: el consentimiento se firma adentro del vr-app después de
 * "Comenzar".
 */
export function consentGateRoute(dest: string): string {
  return `/consent?next=${encodeURIComponent(dest)}`
}

/**
 * Arranque de la experiencia para un JUGADOR: el bridge a la superficie de
 * juego (`/`, ver `pages/PlayBridge.tsx`), que canjea la cookie por un
 * bearer y abre el lobby del vr-app.
 *
 * Ya NO hay cadena de gates acá (decisión de mg 2026-09-07, doc 32 §4):
 * consentimiento, cuestionarios pendientes, sensores y el chequeo previo
 * (obligatorio en cada sesión desde 2026-09-07) corren adentro del vr-app al
 * tocar "Comenzar". El perfil incompleto tampoco bloquea: queda como CTA en
 * `/data`.
 */
export function playerStartRoute(_user: MeResponse): string {
  return '/'
}

/**
 * Landing diferenciado por rol después del login:
 *
 *   0. cuenta SIN CLUB (jugador suelto) → `/sin-club`. Gana sobre todo lo
 *      demás: no hay nada que pueda hacer hasta que un club la dé de alta.
 *   1. `?next=` explícito y seguro (ver `safeNextPath`), PERO pasando antes
 *      por el gate de consentimiento si el jugador todavía no lo firmó (el
 *      `next` viaja colgado del gate, ver `consentGateRoute`). Un `next=` de
 *      staff (`/admin`, `/club`) no pasa por ahí: ese gate es del jugador.
 *   2. staff de club → `/club` (su dashboard).
 *   3. superadmin / device_admin sin club → `/admin`.
 *   4. jugador → `playerStartRoute` (el bridge `/` → lobby del vr-app).
 */
export function postLoginRoute(user: MeResponse, next?: string | null): string {
  // Sin club, antes que nada: ni `?next=`, ni el gate de consentimiento, ni el
  // puente al vr-app tienen sentido para una cuenta que no puede hacer nada.
  if (isClubless(user)) return NO_CLUB_ROUTE
  const explicit = safeNextPath(next)
  if (explicit) {
    if (needsConsent(user) && !isStaffNext(explicit)) {
      return consentGateRoute(explicit)
    }
    return explicit
  }
  if (isClubStaff(user)) return '/club'
  if (hasAdminAreaAccess(user)) return '/admin'
  return playerStartRoute(user)
}
